/**
 * DocumentUploadModal Component
 * Modal for uploading multiple documents to a power with document type selection
 */

import { useState, useRef, useCallback } from 'react';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSpinner, OathAlert } from '../../../shared/OathUI';
import { toast } from 'react-toastify';
import { uploadPowerDocuments } from '../../../../services/oathPowerDocumentService';

/**
 * Document types for power documents
 */
const DOCUMENT_TYPES = [
  { id: 1, name: "Court Doc's" },
  { id: 2, name: 'Forfeitures' },
  { id: 3, name: 'Judgements / Demands' },
  { id: 4, name: 'Discharges' },
  { id: 5, name: 'Set Aside' },
];

// Accepted file types
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * Format file size for display
 */
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentUploadModal = ({ isOpen, onClose, powerNo, onUploadSuccess }) => {
  const [selectedType, setSelectedType] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Reset state when modal closes
  const handleClose = useCallback(() => {
    if (uploading) return;
    setSelectedType('');
    setFiles([]);
    setError(null);
    setIsDragging(false);
    onClose();
  }, [uploading, onClose]);

  // Validate and add files
  const addFiles = useCallback((newFiles) => {
    const validFiles = [];
    const errors = [];

    Array.from(newFiles).forEach(file => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: Invalid type`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: Too large (max 10MB)`);
        return;
      }
      if (files.some(f => f.name === file.name && f.size === file.size)) {
        return; // Skip duplicates silently
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join(', '));
    } else {
      setError(null);
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  }, [files]);

  // Handle file input change
  const handleFileSelect = (event) => {
    if (event.target.files?.length) {
      addFiles(event.target.files);
    }
    event.target.value = '';
  };

  // Handle drag events
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  // Remove a file from the list
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setError(null);
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedType || files.length === 0) return;

    try {
      setUploading(true);
      setError(null);

      const result = await uploadPowerDocuments(powerNo, selectedType, files);

      if (result.success) {
        toast.success(`${files.length} document${files.length > 1 ? 's' : ''} uploaded`);
        onUploadSuccess?.();
        handleClose();
      } else {
        setError(result.error || 'Failed to upload');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const canUpload = selectedType && files.length > 0 && !uploading;

  return (
    <OathModal
      isOpen={isOpen}
      onClose={uploading ? undefined : handleClose}
      title="Upload Documents"
      size="sm"
      footer={
        <div className="flex gap-2 justify-end">
          <OathButton
            variant="outline"
            onClick={handleClose}
            disabled={uploading}
          >
            Cancel
          </OathButton>
          <OathButton
            onClick={handleUpload}
            disabled={!canUpload}
            icon={uploading ? null : CloudArrowUpIcon}
          >
            {uploading ? (
              <span className="flex items-center gap-2">
                <OathSpinner size="sm" />
                Uploading...
              </span>
            ) : (
              `Upload${files.length > 0 ? ` (${files.length})` : ''}`
            )}
          </OathButton>
        </div>
      }
    >
      <div className="space-y-3">
        {/* Error Display */}
        {error && (
          <OathAlert variant="error" onClose={() => setError(null)}>
            {error}
          </OathAlert>
        )}

        {/* Document Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Document Type
          </label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            disabled={uploading}
            className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary focus:border-primary transition-colors disabled:opacity-50"
          >
            <option value="">Select type...</option>
            {DOCUMENT_TYPES.map(type => (
              <option key={type.id} value={type.name}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        {/* File Drop Zone */}
        <div>
          <input
            type="file"
            ref={fileInputRef}
            accept=".pdf,.jpg,.jpeg,.png,.gif"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            disabled={uploading}
            className={`w-full flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed rounded-lg transition-colors disabled:opacity-50 ${
              isDragging
                ? 'border-primary bg-primary/5 dark:bg-primary/10'
                : 'border-gray-300 dark:border-gray-600 hover:border-primary hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <CloudArrowUpIcon className={`h-5 w-5 ${isDragging ? 'text-primary' : 'text-gray-400'}`} />
            <span className={`text-sm ${isDragging ? 'text-primary' : 'text-gray-500 dark:text-gray-400'}`}>
              {isDragging ? 'Drop files' : 'Drop files or click to browse'}
            </span>
          </button>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-center">
            PDF, JPG, PNG (max 10MB)
          </p>
        </div>

        {/* Selected Files List */}
        {files.length > 0 && (
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
              >
                <DocumentIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="flex-1 text-xs text-gray-700 dark:text-gray-300 truncate">
                  {file.name}
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {formatFileSize(file.size)}
                </span>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  disabled={uploading}
                  className="p-2 sm:p-1 text-gray-400 hover:text-red-500 rounded transition-colors disabled:opacity-50"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </OathModal>
  );
};

export default DocumentUploadModal;
